import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { queryKeys } from '../../hooks/queries/keys'
import type { MessageObject } from '../../types/inbox'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { sanitizeHtml } from '../../utils/sanitize-html'
import SendTime from './send-time'

const Message = (props: MessageObject) => {
  const { subject, send_time, content, message_status, id, onSelect } = props
  const [isRead, setIsRead] = useState(message_status === '1')
  const queryClient = useQueryClient()

  const markAsRead = async () => {
    try {
      await api.post('/ajax_data.php', { id, type: 'msgcontent' })
      // Only flip local read-state once the server confirms — otherwise a
      // failed call silently desyncs the list from the server's unread state.
      setIsRead(true)
      // Drop the header/profile-tab badge count with it. Reading a message used
      // to be followed by a route change, which remounted the header and
      // refetched the count; inside the modal nothing remounts, so the badge
      // stayed on its stale number until a reload.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.unread_messages,
      })
    } catch (e) {
      if (e instanceof Error) {
        console.error(e.message)
      }
    }
  }

  const onClick = () => {
    // Select immediately (instant view); mark-read runs in the background.
    if (!isRead) void markAsRead()
    onSelect(props)
  }

  return (
    <div
      onClick={onClick}
      className="flex gap-2.5 mb-2 py-3 px-3.5 cursor-pointer rounded-xl bg-secondary xl:bg-primary transition-opacity active:opacity-80">
      {/* Unread Circle — its own fixed-width column so read and unread rows
          keep the same text alignment */}
      <div className="w-2 shrink-0 pt-[5px]">
        {!isRead && <div className="h-2 w-2 rounded-lg bg-highlight-gold" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          {/* Title — clamped instead of wrapped, so a long subject can never
              push the timestamp into a column of its own */}
          <span
            className={classNames(
              'flex-1 min-w-0 text-sm leading-[18px] font-medium font-[Poppins] line-clamp-2',
              isRead ? 'text-txt' : 'text-highlight-gold',
            )}>
            {subject}
          </span>
          <SendTime value={send_time} />
        </div>
        {/* Content preview */}
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          className={classNames(
            'mt-1 text-xs leading-4 font-normal font-[Poppins] line-clamp-1',
            // "*" styles the children the CMS content brings along
            isRead
              ? 'text-secondary-txt *:text-secondary-txt'
              : 'text-txt *:text-txt',
          )}
        />
      </div>
    </div>
  )
}

export default Message
